import React from "react";
import { useTranslation } from "react-i18next";
import { Download, ExternalLink, MousePointerClick, Send, ShieldCheck } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { SHOPIFY_DIRECT_ENROLL_ENABLED } from "../../../../config/featureFlags";
import { iconList } from "../../../../utils/iconList";
import { cn } from "../../../../utils/cn";
import "../ModernIntegrations.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Alta de Shopify con la app pública "Beartrack App" del Shopify App Store.
//
// El portal no pide nada: el comercio instala la app en su tienda, la abre y
// presiona "Conectar con Beartrack", que lo trae a la pantalla de autorización
// del connect-flow (ConnectStoreView, platform=shopify). Este modal es la guía.
//
// La vía puente (credenciales de una app propia, AddShopifyModal) queda detrás
// de SHOPIFY_DIRECT_ENROLL_ENABLED: solo sirve para re-alta de tiendas ya
// vinculadas por ese camino, y el backend rechaza tiendas nuevas.
// ─────────────────────────────────────────────────────────────────────────────

// Ficha pública de la app. No es configuración por entorno: es una sola app,
// publicada y estable, igual en QA y en producción.
export const SHOPIFY_APP_STORE_URL = "https://apps.shopify.com/beartrack-app";

export default function AddShopifyAppModal({ open, onClose, onUseCredentials }) {
  const { t } = useTranslation();
  const showCredentialsLink =
    SHOPIFY_DIRECT_ENROLL_ENABLED && typeof onUseCredentials === "function";

  const steps = [
    {
      icon: <Download className="h-4 w-4" aria-hidden="true" />,
      text: t("Instala Beartrack App en tu tienda desde el Shopify App Store."),
    },
    {
      icon: <MousePointerClick className="h-4 w-4" aria-hidden="true" />,
      text: t(
        "Abre la app desde Apps en tu admin de Shopify y presiona \"Conectar con Beartrack\".",
      ),
    },
    {
      icon: <ShieldCheck className="h-4 w-4" aria-hidden="true" />,
      text: t(
        "Autoriza la conexión en la pantalla de Beartrack que se abre. Si te pide iniciar sesión, usa tu cuenta de comercio: volverás a esta autorización.",
      ),
    },
    {
      icon: <Send className="h-4 w-4" aria-hidden="true" />,
      // La app no envía nada hasta que el comercio lo activa, y sus filtros por
      // defecto solo aceptan comunas de la RM: sin este paso, un comercio de
      // regiones ve la tienda conectada y ningún pedido. Se nombra el título
      // VISIBLE del interruptor: su label ("Enviar pedidos a Beartrack") está
      // oculto en la app y el comercio no lo encontraría.
      text: t(
        "De vuelta en la app, activa \"Estado de la integración\" y presiona \"Guardar cambios\". Por defecto solo se envían pedidos pagados y sin despachar de comunas de la Región Metropolitana: ajusta los filtros si despachas a otras comunas.",
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t("Conectar {{platform}}", { platform: "Shopify" })}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("Entendido")}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="mig-logo" aria-hidden="true">
            <img src={iconList.shopify} alt="" className="mig-logo__img dark-safe-img" />
          </span>
          <p className="text-sm text-fg-muted">
            {t(
              "Conecta tu tienda con la app oficial de Beartrack: no necesitas crear apps ni copiar credenciales.",
            )}
          </p>
        </div>

        <a
          href={SHOPIFY_APP_STORE_URL}
          target="_blank"
          rel="noreferrer noopener"
          className={cn(
            "inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
            "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700",
          )}
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          {t("Instalar desde Shopify App Store")}
        </a>

        <ol className="space-y-3">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 rounded-lg border border-border bg-bg px-3 py-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bg-muted text-xs font-semibold text-fg">
                {i + 1}
              </span>
              <span className="flex items-start gap-2 text-sm text-fg">
                <span className="mt-0.5 shrink-0 text-fg-muted">{step.icon}</span>
                {step.text}
              </span>
            </li>
          ))}
        </ol>

        <p className="text-xs text-fg-muted">
          {t(
            "Necesitas una cuenta de comercio en Beartrack. Si aún no la tienes, pídesela a tu operador logístico.",
          )}
        </p>

        {showCredentialsLink && (
          <button
            type="button"
            onClick={onUseCredentials}
            className="text-xs text-fg-subtle underline underline-offset-2 hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {t("Conectar con credenciales (solo si soporte te lo indicó)")}
          </button>
        )}
      </div>
    </Modal>
  );
}
